import { useEffect } from "react";
import { useTranslation } from "react-i18next";

// The language of every route that is not wrapped in a LanguageRoute: `lng` in src/i18n/index.ts.
const DEFAULT_LANGUAGE = "en";

// How many LanguageRoutes are mounted right now. Module-level because the one leaving and the one
// arriving are different instances that live in the same commit.
let mountedRoutes = 0;

// Wraps a route to render it in a specific i18next language for the
// duration of that route, then goes back to English when the last
// LanguageRoute unmounts - so navigating away (e.g. via the header's
// language switcher) doesn't leave the rest of the app stuck in "pt".
// This is deliberately per-route rather than a global switcher: only
// pages wrapped here actually have translations to switch to.
function LanguageRoute({ lang, children }: { lang: string; children: React.ReactNode }) {
    const { i18n } = useTranslation();

    useEffect(() => {
        mountedRoutes += 1;
        // Always announced, even when the language is already `lang` (/pt/a -> /pt/b): in React StrictMode's
        // development double-run of effects, react-i18next's own effects can undo an announcement made earlier
        // in the same mount, and this is the one that comes after them.
        i18n.changeLanguage(lang);
        return () => {
            mountedRoutes -= 1;
            // Not restored right here: React runs every unmounting effect before any mounting one, so the
            // English page that replaces this route has rendered (while the language was still "pt") but has
            // not yet subscribed to language changes. Changing the language now would reach nobody, and
            // that page would keep showing Portuguese. A microtask runs once the commit's effects are all
            // done, so the new page hears it and re-renders. If another LanguageRoute has mounted by then
            // (/pt/a -> /pt/b), the language is still the right one and is left alone.
            // (A resolved promise rather than queueMicrotask(), which older browsers in our browserslist lack.)
            Promise.resolve().then(() => {
                if (mountedRoutes === 0) i18n.changeLanguage(DEFAULT_LANGUAGE);
            });
        };
    }, [lang, i18n]);

    return <>{children}</>;
}

export default LanguageRoute;
